import { Call, Device } from '@twilio/voice-sdk';
import { useAppStore } from '@callhub/shared';
import { api } from './api';

export type VoiceCallStatus = 'idle' | 'calling' | 'ringing' | 'in-progress';
export type VoiceCallDirection = 'inbound' | 'outbound' | null;
export type VoiceQualityLevel = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown';
export type VoiceQualityWarning = {
  name: string;
  data: unknown;
  startedAt: number;
};
export type VoiceQualitySnapshot = {
  level: VoiceQualityLevel;
  mos: number | null;
  jitterMs: number | null;
  rttMs: number | null;
  packetLossPct: number | null;
  codec: string | null;
  warnings: VoiceQualityWarning[];
  updatedAt: number | null;
};

export type TwilioVoiceState = {
  device: Device | null;
  isRegistered: boolean;
  activeCall: Call | null;
  callStatus: VoiceCallStatus;
  activeCallDirection: VoiceCallDirection;
  callQuality: VoiceQualitySnapshot;
};

type TwilioVoiceListener = (state: TwilioVoiceState) => void;
type VoiceQualityTier = 'conservative' | 'balanced' | 'quality';
type VoiceQualityProfile = {
  tier: VoiceQualityTier;
  maxAverageBitrate: number;
  dscp: boolean;
};
type TwilioSamplePayload = {
  mos?: number | null;
  jitter?: number;
  rtt?: number;
  packetsLostFraction?: number;
  codecName?: string;
  timestamp?: number;
};

const DEFAULT_CALL_QUALITY: VoiceQualitySnapshot = {
  level: 'unknown',
  mos: null,
  jitterMs: null,
  rttMs: null,
  packetLossPct: null,
  codec: null,
  warnings: [],
  updatedAt: null,
};

let state: TwilioVoiceState = {
  device: null,
  isRegistered: false,
  activeCall: null,
  callStatus: 'idle',
  activeCallDirection: null,
  callQuality: DEFAULT_CALL_QUALITY,
};

const listeners = new Set<TwilioVoiceListener>();
const wiredCalls = new WeakSet<Call>();
let initPromise: Promise<Device | null> | null = null;
let activeQualityProfile: VoiceQualityProfile | null = null;

const DEFAULT_CODEC_PREFERENCES: Call.Codec[] = [Call.Codec.Opus, Call.Codec.PCMU];
const DEFAULT_OUTGOING_RTC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    autoGainControl: true,
    echoCancellation: true,
    noiseSuppression: true,
    channelCount: 1,
  },
  video: false,
};
const DEFAULT_RTC_CONFIGURATION: RTCConfiguration = {
  iceTransportPolicy: 'all',
};
const VOICE_HEARTBEAT_INTERVAL_MS = 30_000;
const VOICE_HEARTBEAT_TTL_MS = 90_000;

let voicePresenceIntervalId: number | null = null;
let voicePresenceSessionKey: string | null = null;
let currentTwilioClientIdentity: string | null = null;

function emit() {
  for (const listener of listeners) {
    listener(state);
  }
}

function updateState(patch: Partial<TwilioVoiceState>) {
  state = { ...state, ...patch };
  emit();
}

function getVoicePresenceSessionKey(): string {
  if (voicePresenceSessionKey) return voicePresenceSessionKey;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    voicePresenceSessionKey = crypto.randomUUID();
    return voicePresenceSessionKey;
  }
  voicePresenceSessionKey = `voice-session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return voicePresenceSessionKey;
}

function decodeBase64UrlPayload(segment: string): string | null {
  if (!segment) return null;
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

  try {
    return atob(padded);
  } catch {
    return null;
  }
}

function resolveClientIdentityFromToken(token: string): string | null {
  const payloadSegment = token.split('.')[1];
  const decodedPayload = decodeBase64UrlPayload(payloadSegment ?? '');
  if (!decodedPayload) return null;

  try {
    const payload = JSON.parse(decodedPayload) as {
      grants?: {
        identity?: unknown;
      };
    };
    const identity =
      typeof payload.grants?.identity === 'string' ? payload.grants.identity.trim() : '';
    return identity.length > 0 ? identity : null;
  } catch {
    return null;
  }
}

function resolveVoiceDeviceLabel(): string | null {
  if (typeof navigator === 'undefined') return null;
  const platform = typeof navigator.platform === 'string' ? navigator.platform.trim() : '';
  const userAgent = typeof navigator.userAgent === 'string' ? navigator.userAgent.trim() : '';
  const label = [platform, userAgent].filter((part) => part.length > 0).join(' | ');
  return label.length > 0 ? label : null;
}

async function sendVoicePresenceHeartbeat(
  registrationStatus: 'registered' | 'offline' | 'errored',
) {
  try {
    await api.sendVoicePresenceHeartbeat({
      sessionKey: getVoicePresenceSessionKey(),
      provider: 'twilio',
      clientIdentity: currentTwilioClientIdentity ?? getVoicePresenceSessionKey(),
      deviceLabel: resolveVoiceDeviceLabel(),
      registrationStatus,
      expiresAt:
        registrationStatus === 'registered'
          ? new Date(Date.now() + VOICE_HEARTBEAT_TTL_MS).toISOString()
          : null,
      metadata: activeQualityProfile
        ? {
            qualityTier: activeQualityProfile.tier,
            maxAverageBitrate: activeQualityProfile.maxAverageBitrate,
            dscp: activeQualityProfile.dscp,
          }
        : null,
    });
  } catch (error) {
    console.warn('[Twilio Voice] presence heartbeat failed', error);
  }
}

function stopVoicePresenceHeartbeat() {
  if (voicePresenceIntervalId === null) return;
  globalThis.clearInterval(voicePresenceIntervalId);
  voicePresenceIntervalId = null;
}

function invalidateTwilioDevice(registrationStatus: 'offline' | 'errored', error?: unknown) {
  updateState({ device: null, isRegistered: false });
  useAppStore.getState().setConnected(false);
  stopVoicePresenceHeartbeat();
  if (error) {
    console.error('Twilio device error:', error);
  }
  void sendVoicePresenceHeartbeat(registrationStatus);
}

function startVoicePresenceHeartbeat() {
  stopVoicePresenceHeartbeat();
  voicePresenceIntervalId = globalThis.setInterval(() => {
    void sendVoicePresenceHeartbeat('registered');
  }, VOICE_HEARTBEAT_INTERVAL_MS) as unknown as number;
}

function normalizeNumber(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return null;
  return value;
}

function degradeQualityLevel(level: VoiceQualityLevel): VoiceQualityLevel {
  if (level === 'excellent') return 'good';
  if (level === 'good') return 'fair';
  if (level === 'fair') return 'poor';
  return level;
}

function determineQualityLevel(
  mos: number | null,
  packetLossPct: number | null,
  jitterMs: number | null,
  rttMs: number | null,
  warningCount: number,
): VoiceQualityLevel {
  let level: VoiceQualityLevel;
  if (mos === null) {
    level = warningCount > 0 ? 'poor' : 'unknown';
  } else if (mos >= 4.2) {
    level = 'excellent';
  } else if (mos >= 4.0) {
    level = 'good';
  } else if (mos >= 3.6) {
    level = 'fair';
  } else {
    level = 'poor';
  }

  if (packetLossPct !== null && packetLossPct >= 5) {
    level = degradeQualityLevel(level);
  }
  if (jitterMs !== null && jitterMs >= 30) {
    level = degradeQualityLevel(level);
  }
  if (rttMs !== null && rttMs >= 300) {
    level = degradeQualityLevel(level);
  }
  if (warningCount > 0 && level === 'excellent') {
    level = 'good';
  }
  if (warningCount > 1 && level === 'good') {
    level = 'fair';
  }

  return level;
}

function resetCallQuality() {
  updateState({ callQuality: DEFAULT_CALL_QUALITY });
}

function updateCallQualityFromSample(sample: TwilioSamplePayload) {
  const mos = normalizeNumber(sample.mos);
  const jitterMs = normalizeNumber(sample.jitter);
  const rttMs = normalizeNumber(sample.rtt);
  const packetLossRatio = normalizeNumber(sample.packetsLostFraction);
  const packetLossPct = packetLossRatio === null ? null : Math.max(0, packetLossRatio * 100);
  const codec = typeof sample.codecName === 'string' ? sample.codecName : null;
  const updatedAt = normalizeNumber(sample.timestamp) ?? Date.now();
  const warningCount = state.callQuality.warnings.length;

  updateState({
    callQuality: {
      ...state.callQuality,
      mos,
      jitterMs,
      rttMs,
      packetLossPct,
      codec,
      updatedAt,
      level: determineQualityLevel(mos, packetLossPct, jitterMs, rttMs, warningCount),
    },
  });
}

function upsertCallWarning(name: string, data: unknown) {
  const warnings = state.callQuality.warnings.slice();
  const existingIndex = warnings.findIndex((warning) => warning.name === name);
  const startedAt = existingIndex >= 0 ? warnings[existingIndex].startedAt : Date.now();
  const nextWarning: VoiceQualityWarning = { name, data, startedAt };

  if (existingIndex >= 0) {
    warnings[existingIndex] = nextWarning;
  } else {
    warnings.push(nextWarning);
  }

  updateState({
    callQuality: {
      ...state.callQuality,
      warnings,
      level: determineQualityLevel(
        state.callQuality.mos,
        state.callQuality.packetLossPct,
        state.callQuality.jitterMs,
        state.callQuality.rttMs,
        warnings.length,
      ),
    },
  });
}

function clearCallWarning(name: string) {
  const warnings = state.callQuality.warnings.filter((warning) => warning.name !== name);
  if (warnings.length === state.callQuality.warnings.length) return;

  updateState({
    callQuality: {
      ...state.callQuality,
      warnings,
      level: determineQualityLevel(
        state.callQuality.mos,
        state.callQuality.packetLossPct,
        state.callQuality.jitterMs,
        state.callQuality.rttMs,
        warnings.length,
      ),
    },
  });
}

function getNetworkConnection():
  | (EventTarget & {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    })
  | null {
  if (typeof navigator === 'undefined') return null;

  const nav = navigator as Navigator & {
    connection?: EventTarget & {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
    mozConnection?: EventTarget & {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
    webkitConnection?: EventTarget & {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
  };

  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
}

function resolveTwilioEdge(): Device.Options['edge'] {
  const raw = import.meta.env.VITE_TWILIO_EDGE?.trim();
  if (!raw) return 'roaming';

  const parsed = raw
    .split(',')
    .map((item: string) => item.trim())
    .filter((item: string) => item.length > 0);

  if (parsed.length === 0) return 'roaming';
  if (parsed.length === 1) return parsed[0];
  return parsed;
}

function resolveVoiceQualityProfile(): VoiceQualityProfile {
  const connection = getNetworkConnection();
  const effectiveType = connection?.effectiveType;
  const downlink = typeof connection?.downlink === 'number' ? connection.downlink : null;
  const rtt = typeof connection?.rtt === 'number' ? connection.rtt : null;
  const saveData = connection?.saveData === true;

  if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') {
    return { tier: 'conservative', maxAverageBitrate: 12000, dscp: false };
  }

  if (
    effectiveType === '3g' ||
    (downlink !== null && downlink < 1.2) ||
    (rtt !== null && rtt > 180)
  ) {
    return { tier: 'balanced', maxAverageBitrate: 18000, dscp: true };
  }

  return { tier: 'quality', maxAverageBitrate: 24000, dscp: true };
}

function buildDeviceOptions(profile: VoiceQualityProfile): Device.Options {
  return {
    logLevel: 1,
    codecPreferences: DEFAULT_CODEC_PREFERENCES,
    dscp: profile.dscp,
    edge: resolveTwilioEdge(),
    enableImprovedSignalingErrorPrecision: true,
    maxAverageBitrate: profile.maxAverageBitrate,
    maxCallSignalingTimeoutMs: 30000,
  };
}

function applyAdaptiveQuality(device: Device) {
  const nextProfile = resolveVoiceQualityProfile();
  const profileChanged =
    !activeQualityProfile ||
    activeQualityProfile.tier !== nextProfile.tier ||
    activeQualityProfile.maxAverageBitrate !== nextProfile.maxAverageBitrate ||
    activeQualityProfile.dscp !== nextProfile.dscp;

  if (!profileChanged) return;

  activeQualityProfile = nextProfile;
  device.updateOptions(buildDeviceOptions(nextProfile));
  console.info(
    `[Twilio Voice] Applied ${nextProfile.tier} quality profile (${nextProfile.maxAverageBitrate}bps)`,
  );
}

function wireCallLifecycle(call: Call) {
  if (wiredCalls.has(call)) return;
  wiredCalls.add(call);

  call.on('accept', () => {
    updateState({ callStatus: 'in-progress' });
  });

  call.on('sample', (sample) => {
    updateCallQualityFromSample(sample as TwilioSamplePayload);
  });

  call.on('warning', (name, data) => {
    upsertCallWarning(name, data);
  });

  call.on('warning-cleared', (name) => {
    clearCallWarning(name);
  });

  call.on('reconnecting', (error) => {
    upsertCallWarning('reconnecting', error);
  });

  call.on('reconnected', () => {
    clearCallWarning('reconnecting');
  });

  call.on('disconnect', () => {
    updateState({ activeCall: null, callStatus: 'idle', activeCallDirection: null });
    resetCallQuality();
  });

  call.on('cancel', () => {
    updateState({ activeCall: null, callStatus: 'idle', activeCallDirection: null });
    resetCallQuality();
  });

  call.on('error', (error) => {
    console.error('Twilio call error:', error);
    updateState({ activeCall: null, callStatus: 'idle', activeCallDirection: null });
    resetCallQuality();
  });
}

export function getTwilioVoiceState(): TwilioVoiceState {
  return state;
}

export function subscribeTwilioVoice(listener: TwilioVoiceListener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

export async function ensureTwilioVoiceDevice(): Promise<Device | null> {
  if (state.device) return state.device;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { token } = await api.getTwilioToken();
      currentTwilioClientIdentity = resolveClientIdentityFromToken(token);
      const initialProfile = resolveVoiceQualityProfile();
      activeQualityProfile = initialProfile;
      const device = new Device(token, buildDeviceOptions(initialProfile));

      device.on('registered', () => {
        updateState({ isRegistered: true });
        useAppStore.getState().setConnected(true);
        void sendVoicePresenceHeartbeat('registered');
        startVoicePresenceHeartbeat();
      });

      device.on('unregistered', () => {
        invalidateTwilioDevice('offline');
      });

      device.on('error', (error) => {
        invalidateTwilioDevice('errored', error);
      });

      device.on('incoming', (call) => {
        updateState({ activeCall: call, callStatus: 'ringing', activeCallDirection: 'inbound' });
        resetCallQuality();
        wireCallLifecycle(call);
      });

      const connection = getNetworkConnection();
      if (connection) {
        const handleConnectionChange = () => applyAdaptiveQuality(device);
        connection.addEventListener('change', handleConnectionChange);
      }

      await device.register();
      updateState({ device });
      return device;
    } catch (error) {
      console.error('Failed to setup Twilio voice device:', error);
      updateState({ device: null, isRegistered: false });
      useAppStore.getState().setConnected(false);
      stopVoicePresenceHeartbeat();
      return null;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

export async function startOutboundCall(destination: string): Promise<Call> {
  const device = await ensureTwilioVoiceDevice();
  if (!device) {
    throw new Error('Voice device unavailable');
  }

  updateState({ callStatus: 'calling', activeCallDirection: 'outbound' });
  resetCallQuality();

  const options: Device.ConnectOptions = {
    params: { To: destination },
    rtcConfiguration: DEFAULT_RTC_CONFIGURATION,
    rtcConstraints: DEFAULT_OUTGOING_RTC_CONSTRAINTS,
  };

  let call: Call;
  try {
    call = await device.connect(options);
  } catch (error) {
    console.warn(
      '[Twilio Voice] connect with tuned RTC constraints failed, retrying default',
      error,
    );
    call = await device.connect({ params: { To: destination } });
  }

  updateState({ activeCall: call, activeCallDirection: 'outbound' });
  wireCallLifecycle(call);
  return call;
}

export function acceptOrHangupCurrentCall() {
  const call = state.activeCall;
  if (!call) return;

  if (state.callStatus === 'ringing') {
    try {
      call.accept({
        rtcConfiguration: DEFAULT_RTC_CONFIGURATION,
        rtcConstraints: DEFAULT_OUTGOING_RTC_CONSTRAINTS,
      });
    } catch (error) {
      console.warn('[Twilio Voice] incoming accept with tuned RTC constraints failed', error);
      call.accept();
    }
    return;
  }

  call.disconnect();
  updateState({ activeCall: null, callStatus: 'idle', activeCallDirection: null });
  resetCallQuality();
}

export function sendDigitsOnActiveCall(digit: string) {
  if (!state.activeCall || state.callStatus !== 'in-progress') return;
  state.activeCall.sendDigits(digit);
}
